import React, { useState } from 'react';

export default function AdsLibrary() {
  const [activeTab, setActiveTab] = useState('google');
  const [selectedBrand, setSelectedBrand] = useState('All');
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [query, setQuery] = useState('');
  const [showGoogle, setShowGoogle] = useState(true);
  const [showMeta, setShowMeta] = useState(true);

  const googleAds = [
    { advertiser: 'Jakamen', headline: 'New Season – up to 40% off', description: 'Elegant menswear collection. Fast delivery.', image: null },
    { advertiser: 'ModaX', headline: 'Special Offers on Suits', description: 'Extra 15% off at checkout on selected items.', image: null },
    { advertiser: 'UrbanMen', headline: 'Winter Collection is Live', description: 'Coats, jackets, knits – limited stock.', image: null },
  ];

  const metaAds = [
    { advertiser: 'Jakamen', headline: 'Formalwear Essentials', description: 'Style at the office, comfort outside.', image: null },
    { advertiser: 'Jakamen', headline: 'New Season Lookbook', description: 'Premium textures, timeless designs.', image: null },
    { advertiser: 'ModaX', headline: 'Shirts – Buy 2 Get 1 Free', description: 'Smart looks for every day.', image: null },
    { advertiser: 'ModaX', headline: 'Winter Special', description: '30% off thermal knitwear.', image: null },
    { advertiser: 'UrbanMen', headline: 'Casual Friday Starts', description: 'Weekend-only promo.', image: null },
    { advertiser: 'UrbanMen', headline: 'New Denim Series', description: 'Stretchy, durable, comfortable.', image: null },
    { advertiser: 'Suitify', headline: 'Tailor‑Made Edit', description: 'Custom fit and flawless cut.', image: null },
    { advertiser: 'Suitify', headline: 'From Office to Evening', description: 'Smart casual outfits now online.', image: null },
  ];

  // Known brands (start with union of Google + Meta advertisers)
  const initialKnown = Array.from(new Set([...googleAds, ...metaAds].map(a => a.advertiser)));
  const [knownBrands, setKnownBrands] = useState(initialKnown);
  const brandDirectory = [
    'Jakamen','ModaX','UrbanMen','Suitify','Vakko','Network','Ipekyol','Beymen','Damat Tween','Altınyıldız','Kiğılı','Mavi','LC Waikiki','DeFacto'
  ];
  const filteredDirectory = brandDirectory
    .filter(b => b.toLowerCase().includes(searchText.toLowerCase()))
    .filter(b => !knownBrands.includes(b));
  const handleAddBrand = (brandName) => {
    if (!brandName) return;
    if (!knownBrands.includes(brandName)) setKnownBrands(prev => [...prev, brandName]);
    setSelectedBrand(brandName);
    setIsAddOpen(false);
    setSearchText('');
  };

  // Platform toggle chip
  const PlatformChip = ({ platform }) => {
    const isGoogle = platform === 'google';
    const active = isGoogle ? showGoogle : showMeta;
    const toggle = () => (isGoogle ? setShowGoogle(!showGoogle) : setShowMeta(!showMeta));
    const activeStyles = 'bg-black text-white border-black';
    return (
      <button
        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition inline-flex items-center gap-2 ${active ? activeStyles : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'}`}
        onClick={toggle}
      >
        <img src={isGoogle ? '/google_ads-512.webp' : '/6033716.png'} alt={isGoogle ? 'Google Ads' : 'Meta'} className="w-4 h-4 object-contain" />
        {isGoogle ? 'Google Ads' : 'Meta'}
      </button>
    );
  };

  const Card = ({ platform, advertiser, headline, description }) => (
    <div className="rounded-xl border bg-white overflow-hidden shadow-sm hover:shadow-md transition">
      <div className="p-3 border-b flex items-center justify-between">
        <div className="flex items-center gap-2">
          {platform === 'google' && <img src="/google_ads-512.webp" alt="Google Ads" className="w-5 h-5 object-contain" />}
          {platform === 'meta' && <img src="/6033716.png" alt="Meta" className="w-5 h-5 object-contain" />} 
          <span className="text-xs text-gray-600">{platform === 'google' ? 'Google Ads' : 'Meta'}</span>
        </div>
        <span className="text-xs font-medium text-gray-900">{advertiser}</span>
      </div>
      <div className="p-4">
        {/* Creative preview placeholder */}
        <div className="h-40 rounded-lg bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center text-gray-400 text-xs mb-3">
          Creative Preview
        </div>
        <div className="space-y-1">
          <div className="text-sm font-semibold text-gray-900">{headline}</div>
          <div className="text-sm text-gray-600">{description}</div>
        </div>
      </div>
      <div className="px-4 py-3 border-t flex items-center justify-between text-xs text-gray-500">
        <span>Dummy • Last seen: recent</span>
        <button className="px-3 py-1 rounded-md border border-gray-200 hover:bg-gray-50">Save</button>
      </div>
    </div>
  );

  const combined = [
    ...(showGoogle ? googleAds.map(a => ({ ...a, platform: 'google' })) : []),
    ...(showMeta ? metaAds.map(a => ({ ...a, platform: 'meta' })) : []),
  ];
  const brands = ['All', ...knownBrands];
  const searched = combined.filter(a => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return [a.advertiser, a.headline, a.description].some(v => (v || '').toLowerCase().includes(q));
  });
  const visible = selectedBrand === 'All' ? searched : searched.filter(a => a.advertiser === selectedBrand);

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        <div className="rounded-2xl overflow-hidden border shadow-sm">
          <div className="p-6 bg-white text-gray-900 border-b flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Ads Library</h1>
              <p className="text-sm text-gray-500">Quick preview of competitors’ active ad creatives</p>
            </div>
            <div className="flex gap-2"></div>
          </div>
          <div className="p-4 border-b bg-gray-50">
            <div className="flex items-center gap-3 flex-wrap">
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-full max-w-sm px-3 py-2 border rounded-lg text-sm"
                placeholder="Search advertiser, headline or text"
              />
              <div className="flex items-center gap-2">
                <PlatformChip platform="google" />
                <PlatformChip platform="meta" />
                {brands.map(b => (
                  <button
                    key={b}
                    className={`px-3 py-1.5 rounded-full text-xs border transition ${selectedBrand === b ? 'bg-black text-white border-black' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'}`}
                    onClick={() => setSelectedBrand(b)}
                  >{b}</button>
                ))}
                <button
                  className="px-3 py-1.5 rounded-full text-xs border border-dashed hover:border-gray-400 text-gray-700 bg-white"
                  onClick={() => setIsAddOpen(true)}
                >+ Add brand</button>
              </div>
            </div>
          </div>
          <div className="px-6 py-4 bg-white">
            {selectedBrand === 'All' ? (
              <div className="space-y-6">
                {Array.from(new Set(visible.map(v => v.advertiser))).map(brand => (
                  <div key={brand} className="rounded-xl border bg-white p-4">
                    <div className="mb-3 flex items-center justify-between">
                      <h2 className="text-lg font-semibold">{brand}</h2>
                      <span className="text-xs text-gray-500">{visible.filter(v => v.advertiser === brand).length} creative</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {visible.filter(v => v.advertiser === brand).map((ad, i) => (
                        <Card key={`${ad.platform}-${brand}-${i}`} platform={ad.platform} {...ad} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {visible.map((ad, i) => (
                  <Card key={`${ad.platform}-${i}`} platform={ad.platform} {...ad} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      {isAddOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={() => setIsAddOpen(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-xl border w-full max-w-lg mx-4">
            <div className="p-5 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">Add Brand</h3>
              <button className="text-gray-500 hover:text-gray-700" onClick={() => setIsAddOpen(false)}>✕</button>
            </div>
            <div className="p-5 space-y-4">
              <input
                autoFocus
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm"
                placeholder="Search brand..."
              />
              <div className="max-h-64 overflow-auto divide-y">
                {filteredDirectory.length ? filteredDirectory.map((name) => (
                  <button
                    key={name}
                    className="w-full text-left px-3 py-2 hover:bg-gray-50"
                    onClick={() => handleAddBrand(name)}
                  >{name}</button>
                )) : (
                  <div className="px-3 py-8 text-center text-sm text-gray-500">No results</div>
                )}
              </div>
              {!!searchText && !brandDirectory.some(b => b.toLowerCase() === searchText.toLowerCase()) && (
                <div className="flex justify-end">
                  <button className="px-3 py-2 rounded-lg bg-black text-white text-sm" onClick={() => handleAddBrand(searchText.trim())}>Add “{searchText.trim()}”</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


